// src/api/acts.js
import { apiGet, apiPatch } from 'lib/apiClient'

/**
 * Список актов с фильтрами и пагинацией.
 * params:
 *  - type: 'in'|'out'|'return'|'' (все)
 *  - q: поиск по № акта или ФИО app_user
 *  - dateFrom/dateTo: ISO строки (например, '2025-01-01T00:00:00.000Z')
 *  - limit, offset: пагинация
 * return: { rows, count }
 */
export async function listActs({
    type = '',
    q = '',
    dateFrom = '',
    dateTo = '',
    limit = 50,
    offset = 0,
} = {}) {
    // Backend expects 'page' instead of 'offset'
    const page = Math.floor(offset / limit) + 1;
    const params = { limit, page };
    if (type) params.type = type;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (q) params.q = q;

    const { data, error } = await apiGet('/acts/', params);
    if (error) throw new Error(error);

    const rows = (data.rows || []).map((r) => ({
        id: r.id,
        act_no: r.act_no,
        act_type: r.act_type,
        created_at: r.created_at,
        pdf_url: r.pdf_url,
        app_user_id: r.app_user_id,
        app_user_name: r.app_user_name ?? null,
        app_user_photo_url: r.app_user_photo_url ?? null,
        counterparty: r.counterparty ?? null,
    }));

    return { rows, count: data.count || 0 };
}

/**
 * Получить детали акта + позиции (движения) для генерации PDF.
 * Возвращает:
 *  {
 *    act_no, act_type, created_at,
 *    counterparty, app_user_name,
 *    positions: [{name, unit, qty, price|null}]
 *  }
 */
export async function getActDetails(actId) {
    const { data, error } = await apiGet(`/acts/${actId}/details/`);
    if (error) throw new Error(error);
    return data;
}

/** Прочитать настройки организации (заголовок PDF) */
export async function getOrgForPdf() {
    const { data, error } = await apiGet('/settings/org/');
    if (error && !error.includes('404')) throw new Error(error);
    return data || { id: 1, org_name: '', inn: '', address: '', phone: '', director_name: '', logo_url: '' };
}

/** Обновить ссылку на PDF у акта */
export async function setActPdfUrl(actId, url) {
    const { error } = await apiPatch(`/acts/${actId}/set_pdf_url/`, { pdf_url: url });
    if (error) throw new Error(error);
}

/**
 * Загрузка PDF в bucket 'acts' и возврат URL.
 * NOTE: Bu funksiya hozircha ishlamaydi, chunki file storage hali sozlanmagan.
 * Keyinroq Django media files yoki cloud storage qo'shish mumkin.
 */
export async function uploadActPdf(actId, filename, blob) {
    // TODO: Implement file upload to Django backend
    // For now, return a placeholder URL
    const year = new Date().getFullYear();
    const path = `${year}/${actId}/${filename}`;

    // This would need to be implemented in Django backend
    // For now, we'll use a local URL pattern
    const url = `/media/acts/${path}`;

    // Update the act with the PDF URL
    await setActPdfUrl(actId, url);
    return url;
}
